import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';

import { CommandGroup } from 'components/commandPalette/CommandGroup';
import Icon from 'components/lib/ui/Icon';

import { useCommandRegistry, useGetCommandsByGroup } from 'lib/commands/context';
import type { MonarchCommand, MonarchCommandGroup } from 'lib/commands/types';
import { ShortcutType } from 'lib/keyboardShortcuts';

import type { SidebarRouteName } from 'constants/sidebar';
import { SIDEBAR_ROUTES } from 'constants/sidebar';

const GROUP: MonarchCommandGroup = 'navigation';
const SIDEBAR_ROUTE_TO_SHORTCUT_MAP: Partial<Record<SidebarRouteName, string[]>> = {
  Dashboard: ['g', 'd'],
  Transactions: ['g', 't'],
  Reports: ['g', 'r'],
  Budget: ['g', 'b'],
  Recurring: ['g', 'c'],
  Accounts: ['g', 'a'],
  'Cash Flow': ['g', 'f'],
  Goals: ['g', 'g'],
  Investments: ['g', 'i'],
};

/**
 * A component that renders all navigation-related commands in the command palette.
 */
export const NavigationCommands: React.FC = () => {
  const commands = useGetCommandsByGroup(GROUP);

  return (
    <CommandGroup.Root group={GROUP} heading="Navigation">
      {commands.map((command) => (
        <CommandGroup.Item key={command.id} {...command} />
      ))}
    </CommandGroup.Root>
  );
};

/**
 * A hook that registers all navigation-related commands.
 * Separate from the UI rendering component.
 */
export const useRegisterNavigationCommands = (): void => {
  const { registerCommand } = useCommandRegistry();
  const history = useHistory();

  const maybeNavigateToPath = useCallback(
    (path: string) => {
      if (history.location.pathname !== path) {
        history.push(path);
      }
    },
    [history],
  );

  const commands = useMemo(
    () =>
      Object.entries(SIDEBAR_ROUTES)
        // Only include routes that have a shortcut defined
        .filter(([route]) => SIDEBAR_ROUTE_TO_SHORTCUT_MAP[route as SidebarRouteName])
        .map<MonarchCommand>(([route, { route: path, icon }]) => ({
          id: `navigation.${route.toLowerCase()}`,
          label: `Go to ${route}`,
          description: `Navigate to ${route}`,
          keys: SIDEBAR_ROUTE_TO_SHORTCUT_MAP[route as SidebarRouteName],
          action: () => maybeNavigateToPath(path),
          icon: <Icon name={icon} />,
          shortcutType: ShortcutType.SEQUENCE,
        })),
    [maybeNavigateToPath],
  );

  // Register all navigation commands
  useEffect(() => {
    commands.forEach((command) => registerCommand(command, GROUP));
  }, [registerCommand, commands]);
};
